import { Head, Link } from "@inertiajs/react"
import { Package } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { ConfirmAddressRefusalProvider } from "@/components/shipments/confirm-address-refusal"
import {
  MarkShippedDialog,
  type MarkShippedDialogRow,
  type MarkShippedTarget,
} from "@/components/shipments/mark-shipped-dialog"
import { ProductChoiceRefusalProvider } from "@/components/shipments/product-choice-refusal"
import { ShipRefusalProvider } from "@/components/shipments/ship-refusal"
import {
  ShipmentParcelCard,
  parcelHeaderId,
} from "@/components/shipments/shipment-parcel-card"
import { ShipmentRowContent } from "@/components/shipments/shipment-row"
import { ShipmentsSection } from "@/components/shipments/shipments-section"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import AppLayout from "@/layouts/app-layout"
import { shippableVariantsFor } from "@/lib/shippable-variants"
import { brandShipmentsPath, missionsPath } from "@/routes"
import type { Locale } from "@/types"
import type {
  ShipmentParcel,
  ShipmentProduct,
  ShipmentRow,
  ShipmentsPageProps,
} from "@/types/shipments"

/**
 * OKS-984 — the unified Shipments queue (`/brand/shipments`).
 *
 * One page answering the two questions an operator has at 9am: who is waiting,
 * and where do I send it.
 *
 * # Sections (UX Spec §2.1)
 *
 *   1. **Ready to ship** — a per-collab address exists. Paginated 25/page.
 *   2. **Waiting on creator** — no per-collab address. Capped at 25 + count.
 *   3. **Recently shipped** — `Collapsible`, collapsed by default, 20 most
 *      recent, so tracking stays correctable before delivery.
 *
 * ⚠️ **Ready INCLUDES snapshot-but-never-confirmed rows.** That is the
 * mandatory intermediate step of the creator's own flow, not an edge — they
 * typed an address and have not pressed confirm. Those rows carry a NEUTRAL
 * note; the amber one is reserved for the profile fallback in Waiting. See
 * `ShipmentAddressBlock`.
 *
 * # States (UX Spec §3)
 *
 *   - **Empty (page)** — `EmptyState` with `titleAs="h2"` (the page owns the
 *     `<h1>`), only when all three sections are empty.
 *   - **Empty (section)** — one muted line inside the Card, never a full
 *     EmptyState, which inside a populated page reads as an error.
 *   - **Loading** — first paint is server-rendered; Inertia preserves the
 *     previous list on subsequent visits. Skeletons exist for the cold case
 *     (`ShipmentsSectionSkeleton`) and are never a spinner.
 *   - **Error** — Inertia's error overlay for a failed visit. A refused ship
 *     keeps its dialog open (302 + errors bag, never a toast): a field refusal
 *     renders under its field, anything else as the dialog-level `errors.base`
 *     message (OKS-1006). Before OKS-1006 nothing rendered `base` on this page,
 *     so a refused parcel left the dialog open and silent.
 *   - **Success** — the mutation redirects back here, so counts and the
 *     sidebar badge are server-rendered fresh rather than patched client-side.
 *
 * # ⚠️ No localStorage
 *
 * The Recently-shipped collapse is component state only. It resets on
 * navigation, deliberately — a per-device persisted preference here would need
 * one of the two ratified carve-outs, and a shipping queue is not one.
 */
export default function ShipmentsIndex({
  ready_parcels: readyParcels,
  waiting,
  recently_shipped: recentlyShipped,
  counts,
  waiting_truncated: waitingTruncated,
  pagination,
  locale,
}: ShipmentsPageProps & { locale: Locale }) {
  const { t } = useTranslation()
  const [showShipped, setShowShipped] = useState(false)

  // OKS-984 (UX Spec §6) — move focus to a section's heading when a row LEAVES
  // it.
  //
  // ⚠️ This is the behaviour `shipments-section.tsx`'s `tabIndex={-1}` exists
  // for, and it was missing: the attribute made the heading focusABLE, nothing
  // ever focused it, so a keyboard or screen-reader operator dropped to
  // `<body>` the moment a row they had just shipped was removed.
  //
  // Keyed on the count DECREASING, not on any change. A row arriving (someone
  // else's ship, a poll) must not steal focus from whatever the operator is
  // doing — only the disappearance of the element they were acting on
  // justifies moving them.
  //
  // The mark-shipped mutation redirects back to this page, and Inertia swaps
  // props on the same component rather than remounting, so this effect fires
  // on the visit that follows the ship.
  const readyHeadingRef = useRef<HTMLHeadingElement | null>(null)
  const waitingHeadingRef = useRef<HTMLHeadingElement | null>(null)
  // ⚠️ Keyed on ITEMS, not parcels. A parcel ship removes one card but N
  // items; an item shipped alone out of a group removes NO card at all while
  // the operator is still owed feedback. Items decrease in both cases, so the
  // item basis is the one that tracks "something the operator was acting on
  // left the section".
  const previousCounts = useRef({
    ready: counts.ready_items,
    waiting: counts.waiting,
  })

  useEffect(() => {
    const previous = previousCounts.current

    if (counts.ready_items < previous.ready) {
      readyHeadingRef.current?.focus()
    } else if (counts.waiting < previous.waiting) {
      waitingHeadingRef.current?.focus()
    }

    previousCounts.current = {
      ready: counts.ready_items,
      waiting: counts.waiting,
    }
  }, [counts.ready_items, counts.waiting])

  const pageIsEmpty =
    counts.ready_items === 0 && counts.waiting === 0 && counts.shipped === 0

  return (
    <AppLayout>
      <Head title={t("shipments.head_title")} />

      <div className="flex flex-col gap-6 p-4">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{t("shipments.title")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("shipments.subtitle")}
          </p>
        </header>

        {/* OKS-1037 D1 — renders a confirm-address refusal whose dialog left
            with its row (see the provider). A Fragment-transparent wrapper, so
            the sections stay direct flex children. */}
        <ConfirmAddressRefusalProvider>
          {/* OKS-1040 (PO re-walk R1) — a mark-shipped refusal whose parcel
              left Ready renders here, above the queue, once. */}
          <ShipRefusalProvider>
            {/* OKS-1040 (PO p4c-02a) — a « Choisir pour le créateur » refusal
                whose row left Waiting renders here, once. */}
            <ProductChoiceRefusalProvider>
              {pageIsEmpty ? (
                <EmptyState
                  icon={Package}
                  titleAs="h2"
                  title={t("shipments.empty.title")}
                  description={t("shipments.empty.description")}
                  data-testid="shipments-empty"
                  actions={
                    <Button asChild variant="outline">
                      <Link href={missionsPath()}>
                        {t("shipments.empty.cta")}
                      </Link>
                    </Button>
                  }
                />
              ) : (
                <>
                  {/* ⚠️ TWO numbers on TWO bases, both stated out loud (PRD §3).
                The heading leads with parcels because the operator packs
                boxes; the sidebar badge counts items on its own independent
                path. They WILL differ, and that disagreement is deliberate —
                it is the only thing that can contradict this section. */}
                  <ShipmentsSection
                    headingRef={readyHeadingRef}
                    id="ready"
                    title={t("shipments.sections.ready")}
                    count={counts.ready_parcels}
                    // ⚠️ BOTH bases, both named (PRD §3). They differ on purpose:
                    // the operator packs boxes, so parcels lead; the sidebar badge
                    // counts items on its own untouched path.
                    countLabel={`${t("shipments.parcel.parcels", {
                      count: counts.ready_parcels,
                    })} · ${t("shipments.parcel.items", {
                      count: counts.ready_items,
                    })}`}
                    emptyLine={t("shipments.sections.ready_empty")}
                    footer={
                      pagination.pages > 1 ? (
                        <QueuePagination pagination={pagination} />
                      ) : null
                    }
                  >
                    <ul className="flex flex-col gap-4">
                      {readyParcels.map((parcel) => (
                        <li
                          key={parcel.key}
                          className="rounded-xl border p-3 md:p-4"
                          // UX §6 (binding) — the group header is the card's
                          // accessible label. Derived via `parcelHeaderId` rather
                          // than a second template string, so this and the id it
                          // points at cannot drift apart into a silently inert
                          // reference.
                          aria-labelledby={parcelHeaderId(parcel.key)}
                        >
                          <ShipmentParcelCard
                            parcel={parcel}
                            locale={locale}
                            action={
                              <MarkShippedDialog
                                row={toParcelDialogRow(parcel)}
                                target={{
                                  kind: "parcel",
                                  items: parcel.items.map((item) => ({
                                    source: item.source,
                                    id: item.id,
                                  })),
                                }}
                                product={null}
                                parcelItems={parcel.items.map((item) => ({
                                  key: `${item.source}-${item.id}`,
                                  product: dialogProductFor(item),
                                }))}
                                /* ⚠️ EMPTY ON PURPOSE, and this is the one caller
                             where that is still correct. A parcel has no
                             single variant: each item carries its own set on
                             its own product block, posted as
                             `items[i][variant_id]`. A value here would render
                             a parcel-level control that could only ever be
                             right for one item in the box. */
                                variantOptions={[]}
                                returnTo="shipments"
                                // OKS-1028 D4 — the 44px mobile floor `size="sm"`
                                // does not carry (OKS-874 deferral), as on the panel
                                // trigger. This is the parcel CTA: also the only door
                                // for a single-item parcel.
                                triggerClassName="max-md:min-h-11"
                              />
                            }
                            renderItemAction={(row) => (
                              <MarkShippedDialog
                                row={toDialogRow(row)}
                                target={targetFor(row)}
                                product={dialogProductFor(row)}
                                // OKS-989 — the row's OWN shippable set. Collapsed
                                // server-side to the creator's pick when there is
                                // one, so this surface never offers an alternative
                                // to what the creator chose.
                                variantOptions={shippableVariantsFor(
                                  row.source === "collab" ? row.product : null,
                                )}
                                returnTo="shipments"
                                // ⚠️ GHOST, not the default filled primary: this
                                // must read as secondary to the group CTA below it
                                // (UX §2/§4). A filled button here points the
                                // operator at shipping items singly, which is what
                                // grouping exists to stop.
                                triggerVariant="ghost"
                                // OKS-1028 D4 — same floor as the parcel CTA above.
                                triggerClassName="max-md:min-h-11"
                                triggerLabel={t("shipments.parcel.ship_alone")}
                                triggerAriaLabel={t(
                                  "shipments.parcel.ship_alone_aria",
                                  { product: productNameFor(row) },
                                )}
                              />
                            )}
                          />
                        </li>
                      ))}
                    </ul>
                  </ShipmentsSection>

                  <ShipmentsSection
                    headingRef={waitingHeadingRef}
                    id="waiting"
                    title={t("shipments.sections.waiting")}
                    count={counts.waiting}
                    // Waiting is a flat ITEM list — unchanged by grouping, and it
                    // says so rather than showing a bare number.
                    countLabel={t("shipments.parcel.items", {
                      count: counts.waiting,
                    })}
                    emptyLine={t("shipments.sections.waiting_empty")}
                    footer={
                      waitingTruncated ? (
                        <p
                          className="text-muted-foreground mt-3 text-sm"
                          data-testid="shipments-waiting-truncated"
                        >
                          {t("shipments.sections.waiting_showing", {
                            shown: waiting.length,
                            total: counts.waiting,
                          })}
                        </p>
                      ) : null
                    }
                  >
                    <RowList rows={waiting} section="waiting" locale={locale} />
                  </ShipmentsSection>

                  <Collapsible open={showShipped} onOpenChange={setShowShipped}>
                    <ShipmentsSection
                      id="recently-shipped"
                      title={t("shipments.sections.shipped")}
                      count={counts.shipped}
                      countLabel={t("shipments.parcel.items", {
                        count: counts.shipped,
                      })}
                      emptyLine={t("shipments.sections.shipped_empty")}
                    >
                      {/* ⚠️ Base UI `render`, not Radix `asChild`. */}
                      <CollapsibleTrigger
                        render={
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="shipments-recently-shipped-toggle"
                          />
                        }
                      >
                        {showShipped
                          ? t("shipments.sections.hide")
                          : t("shipments.sections.show")}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <RowList
                          rows={recentlyShipped}
                          section="shipped"
                          locale={locale}
                          /* ⚠️ COLLAB ROWS ONLY. UX Spec §2.2: redemption rows in
                       Recently shipped are read-only — "no edit pencil …
                       no disabled control, just no control".

                       Not cosmetic. `Redemptions::MarkShipped` refuses any
                       non-pending row (`return failure(:already_shipped)`),
                       so the control was RENDERED, ENABLED, and did NOTHING:
                       the operator corrects a tracking number, submits, sees
                       no error of any kind, and reasonably concludes it
                       saved while the record is unchanged. Their belief and
                       the data diverge with no signal — worse than the
                       toast-only failure UX Spec §3 already forbids, and in
                       the one section that exists FOR correction.

                       PRD OQ-2 kept redemption tracking-correction out of
                       scope precisely because the service refuses it; adding
                       the affordance without the backend is how that decision
                       leaked into the UI. Found by PO's timed walk. */
                          renderAction={(row) =>
                            row.source === "collab" ? (
                              <MarkShippedDialog
                                row={toDialogRow(row)}
                                target={targetFor(row)}
                                mode="edit"
                                product={dialogProductFor(row)}
                                /* OKS-996 — in `edit` mode this renders the
                             correction select whenever it holds 2+. A shipped
                             row's set is the full shippable one rather than the
                             pick-collapsed one, so that is the normal case
                             here. Corrections are resolved server-side. */
                                variantOptions={shippableVariantsFor(
                                  row.source === "collab" ? row.product : null,
                                )}
                                returnTo="shipments"
                              />
                            ) : null
                          }
                        />
                      </CollapsibleContent>
                    </ShipmentsSection>
                  </Collapsible>
                </>
              )}
            </ProductChoiceRefusalProvider>
          </ShipRefusalProvider>
        </ConfirmAddressRefusalProvider>
      </div>
    </AppLayout>
  )
}

function RowList({
  rows,
  section,
  locale,
  renderAction,
}: {
  rows: ShipmentRow[]
  section: "ready" | "waiting" | "shipped"
  locale: Locale
  renderAction?: (row: ShipmentRow) => React.ReactNode
}) {
  return (
    <ul className="flex flex-col gap-4">
      {rows.map((row) => (
        <li
          key={`${row.source}-${row.id}`}
          className="rounded-xl border p-3 md:p-4"
        >
          <ShipmentRowContent
            row={row}
            section={section}
            locale={locale}
            action={renderAction?.(row)}
          />
        </li>
      ))}
    </ul>
  )
}

function QueuePagination({
  pagination,
}: {
  pagination: ShipmentsPageProps["pagination"]
}) {
  return (
    <Pagination className="mt-4">
      <PaginationContent>
        {pagination.prev && (
          <PaginationItem>
            <PaginationPrevious
              href={`${brandShipmentsPath()}?page=${pagination.prev}`}
            />
          </PaginationItem>
        )}
        {pagination.next && (
          <PaginationItem>
            <PaginationNext
              href={`${brandShipmentsPath()}?page=${pagination.next}`}
            />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  )
}

/**
 * Adapts a queue row to `MarkShippedDialogRow`.
 *
 * ⚠️ The dialog is REUSED, not reimplemented — it already owns the Inertia
 * `<Form>`, the tracking-length counter, the variant select and the error
 * rendering. A second dialog would be a second place for the Crew form rules
 * to be got wrong.
 *
 * ⚠️ NO CAST. The first draft returned `as never` to satisfy the dialog's
 * original `BrandMissionCollabRow` prop — which silences the compiler on
 * exactly the mismatch the type exists to surface. The dialog's prop was
 * narrowed to what it actually reads instead, so both callers satisfy it
 * honestly and a future field the dialog needs fails at compile time here
 * rather than rendering blank.
 *
 * ⚠️ Each row carries its OWN shippable set (`product.shippable_variants`, via
 * `shippableVariantsFor`), which is what lets an unpinned multi-variant mission
 * offer its full set here — the row's display summary is empty for exactly that
 * case and cannot serve the select. The queue's rows span missions, so this was
 * always the only shape available here; OKS-995 brought the mission panel onto
 * the same per-row read.
 */
/**
 * OKS-985 — where this row's mark-shipped form posts. Derived from the row's
 * own `source` rather than passed down, so the page cannot send a redemption
 * to the collab endpoint by threading the wrong prop through a branch.
 */
function targetFor(row: ShipmentRow): MarkShippedTarget {
  return row.source === "collab"
    ? { kind: "collab", missionId: row.mission_id }
    : { kind: "redemption" }
}

function toDialogRow(row: ShipmentRow): MarkShippedDialogRow {
  return {
    id: row.id,
    tracking_number: row.tracking_number,
    tracking_url: row.tracking_url,
    delivery_eta: row.delivery_eta,
    // OKS-987 — this was DROPPED here, so the dialog's `edit`-mode readonly
    // variant line has existed since OKS-934 and never once rendered on this
    // surface: the component branched on a key the adapter did not pass.
    // Nothing failed, because a missing optional field renders nothing.
    shipped_variant_label:
      row.source === "collab" ? row.shipped_variant_label : null,
    // OKS-1028 — the same drop, one field over. `correctionOffered` reads the
    // recorded ID; without it every "records the one offered variant" row
    // offers a control over a settled record, and nothing fails because the
    // field is optional. `correction-rule-mount-oks1028.test.tsx` AC3 reds on
    // the drop.
    shipped_product_variant_id:
      row.source === "collab" ? (row.shipped_product_variant_id ?? null) : null,
    creator: { name: row.creator.name, handle: row.creator.handle },
  }
}

/**
 * OKS-987 — the parcel the dialog's header strip states.
 *
 * ⚠️ Both row types resolve to ONE shape rather than the dialog learning two.
 * A collab carries a real product block; a redemption has no mission product
 * and no variant table, so its reward fields are adapted into the same shape
 * (`Qty 1`, `none`) rather than duplicated under second names on the wire.
 */
function dialogProductFor(row: ShipmentRow): ShipmentProduct | null {
  if (row.source === "collab") return row.product ?? null

  return {
    name: row.reward_title,
    photo_url: row.reward_cover_url,
    quantity: 1,
    variant: { mode: "none", variants: [], count: 0 },
    // OKS-989 — a reward has no variant table at all, so there is nothing that
    // may be recorded as shipped and the dialog offers no control. Empty here
    // is the PERMANENT truth about redemptions, not a row awaiting a wire
    // field: `Shipments::ProductPayload` returns `{}` for a Redemption and
    // this block is synthesised client-side from the reward's own columns.
    shippable_variants: [],
  }
}

/**
 * OKS-991 — the parcel's dialog identity: the creator, and no tracking to
 * prefill.
 *
 * ⚠️ `tracking_number: null` deliberately. A parcel ship is always a first
 * ship (`mark` mode), never a correction — tracking edits stay per-item in
 * Recently-shipped, where the row IS the unit.
 */
function toParcelDialogRow(parcel: ShipmentParcel) {
  return {
    id: parcel.items[0].id,
    tracking_number: null,
    tracking_url: null,
    delivery_eta: null,
    shipped_variant_label: null,
    shipped_product_variant_id: null,
    creator: {
      name: parcel.creator.name,
      handle: parcel.creator.handle,
    },
  }
}

/** The name « Expédier seul » announces, so the action names what it ships. */
function productNameFor(row: ShipmentRow): string {
  return row.source === "collab"
    ? (row.product?.name ?? row.mission_title)
    : row.reward_title
}
